import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { StreamableHTTPServerTransport } from '@modelcontextprotocol/sdk/server/streamableHttp.js';
import type { Request, Response, Router } from 'express';
import type { McpToolEntry } from './registry.js';
import { resolveTools, buildInputSchema } from './registry.js';
import type { TsoaRouteEntry } from './registry.js';

export interface TsoaMcpOptions {
  /** Name of the MCP server exposed to clients */
  name: string;
  /** Version string */
  version: string;
  /** The generated tsoaRouteRegistry array */
  registry: TsoaRouteEntry[];
  /** Path to mount the MCP endpoint on. Defaults to "/mcp" */
  path?: string;
}

/**
 * Creates a stateless MCP server from a TSOA route registry and mounts it on an Express router.
 *
 * Stateless mode: each POST creates a fresh McpServer + transport, handles the request,
 * and tears down. No session tracking, no shared state — safe for distributed deployments
 * behind a load balancer.
 *
 * Only routes whose controller methods are decorated with @Tool are exposed.
 *
 * @example
 * ```typescript
 * import { tsoaRouteRegistry } from './build/routes';
 * import { mountMcp } from 'tsoa-mcp';
 *
 * mountMcp(app, {
 *   name: 'my-api',
 *   version: '1.0.0',
 *   registry: tsoaRouteRegistry,
 * });
 * ```
 */
export function mountMcp(app: Router, opts: TsoaMcpOptions): void {
  const tools = resolveTools(opts.registry);
  const mcpPath = opts.path ?? '/mcp';

  // Debug: log resolved tools at mount time
  console.log(`[tsoa-mcp] Mounting stateless MCP server "${opts.name}" at ${mcpPath}`);
  console.log(`[tsoa-mcp] Registry: ${opts.registry.length} total routes, ${tools.length} @Tool-decorated`);
  for (const tool of tools) {
    const schema = buildInputSchema(tool.args);
    const paramNames = Object.keys(schema.properties);
    console.log(`[tsoa-mcp]   - ${tool.name} (${tool.method.toUpperCase()} ${tool.path}) params=[${paramNames.join(', ')}]`);
  }

  function createServer(): McpServer {
    const server = new McpServer(
      { name: opts.name, version: opts.version },
    );

    for (const tool of tools) {
      const inputSchema = buildInputSchema(tool.args);
      registerMcpTool(server, tool, inputSchema);
    }

    return server;
  }

  // POST /mcp — stateless: each request gets a fresh server + transport
  app.post(mcpPath, async (req: Request, res: Response) => {
    console.log(`[tsoa-mcp] POST ${mcpPath} body=`, JSON.stringify(req.body)?.slice(0, 200));

    try {
      const transport = new StreamableHTTPServerTransport({
        sessionIdGenerator: undefined, // stateless — no session IDs
      });

      const server = createServer();
      await server.connect(transport);

      await transport.handleRequest(req, res, req.body);

      // Clean up after response completes
      res.on('finish', () => {
        transport.close();
        server.close();
      });
    } catch (err) {
      console.error(`[tsoa-mcp] Error handling POST:`, err);
      if (!res.headersSent) {
        res.status(500).json({
          jsonrpc: '2.0',
          error: { code: -32603, message: 'Internal server error' },
          id: null,
        });
      }
    }
  });

  // GET /mcp — not supported in stateless mode (no SSE sessions)
  app.get(mcpPath, (_req: Request, res: Response) => {
    res.status(405).json({
      jsonrpc: '2.0',
      error: { code: -32000, message: 'Method not allowed in stateless mode' },
      id: null,
    });
  });

  // DELETE /mcp — not supported in stateless mode (no sessions to terminate)
  app.delete(mcpPath, (_req: Request, res: Response) => {
    res.status(405).json({
      jsonrpc: '2.0',
      error: { code: -32000, message: 'Method not allowed in stateless mode' },
      id: null,
    });
  });
}

/**
 * Register a single tool on the MCP server.
 * When called, it runs the TSOA middleware chain and controller invocation.
 */
function registerMcpTool(
  server: McpServer,
  tool: McpToolEntry,
  inputSchema: ReturnType<typeof buildInputSchema>,
): void {
  server.registerTool(
    tool.name,
    {
      description: tool.description,
      inputSchema: inputSchema as any,
    },
    async (params: Record<string, unknown>) => {
      return await invokeTool(tool, params);
    },
  );
}

/**
 * Invoke a tool by running its TSOA middleware chain and controller.
 * Returns an MCP CallToolResult.
 */
async function invokeTool(
  tool: McpToolEntry,
  params: Record<string, unknown>,
): Promise<{ content: Array<{ type: 'text'; text: string }> }> {
  return new Promise((resolve, reject) => {
    const reqData = buildRequestData(tool, params);

    const fakeReq = {
      method: tool.method.toUpperCase(),
      url: tool.path,
      path: tool.path,
      params: reqData.params,
      query: reqData.query,
      body: reqData.body,
      headers: {} as Record<string, string | undefined>,
      get(_headerName: string) { return undefined; },
    } as any;

    let statusCode = 200;
    let responseBody: unknown;

    const fakeRes = {
      statusCode: 200,
      headersSent: false,
      writableEnded: false,
      status(code: number) {
        statusCode = code;
        this.statusCode = code;
        return this;
      },
      json(body: unknown) {
        responseBody = body;
        this.headersSent = true;
        this.writableEnded = true;
        finish();
        return this;
      },
      send(body: unknown) {
        responseBody = body;
        this.headersSent = true;
        this.writableEnded = true;
        finish();
        return this;
      },
      setHeader() { return this; },
      getHeader() { return undefined; },
      end(chunk?: unknown) {
        if (chunk) responseBody = chunk;
        this.headersSent = true;
        this.writableEnded = true;
        finish();
        return this;
      },
    } as any;

    let finished = false;
    function finish() {
      if (finished) return;
      finished = true;

      if (statusCode >= 400) {
        const errorText = typeof responseBody === 'object'
          ? JSON.stringify(responseBody)
          : String(responseBody ?? 'Unknown error');
        resolve({
          content: [{ type: 'text', text: `Error ${statusCode}: ${errorText}` }],
        });
      } else {
        // Run the MCPRoute resolver to transform the response
        const resolved = tool.resolver.resolve(responseBody);
        const text = typeof resolved === 'object'
          ? JSON.stringify(resolved)
          : String(resolved ?? '');
        resolve({
          content: [{ type: 'text', text }],
        });
      }
    }

    const next = (err?: any) => {
      if (err) {
        resolve({
          content: [{ type: 'text', text: `Error: ${err.message || String(err)}` }],
        });
        return;
      }
    };

    const middlewares = tool.getMiddlewares();
    runMiddlewareChain(fakeReq, fakeRes, middlewares, () => {
      tool.invoke(fakeReq, fakeRes, next).catch(reject);
    });
  });
}

/**
 * Run an array of Express middlewares sequentially
 */
function runMiddlewareChain(
  req: any,
  res: any,
  middlewares: any[],
  done: () => void,
): void {
  let i = 0;

  function next(err?: any) {
    if (err) {
      res.status(err.status || 500);
      res.json({ error: err.message || String(err) });
      return;
    }

    if (res.writableEnded) return;

    if (i >= middlewares.length) {
      done();
      return;
    }

    const mw = middlewares[i++];
    try {
      mw(req, res, next);
    } catch (e) {
      next(e);
    }
  }

  next();
}

/**
 * Map MCP tool params to Express request locations based on TSOA arg schema
 */
function buildRequestData(
  tool: McpToolEntry,
  params: Record<string, unknown>,
): { params: Record<string, any>; query: Record<string, any>; body: any } {
  const result = { params: {} as any, query: {} as any, body: {} as any };

  for (const [key, schema] of Object.entries(tool.args)) {
    if (schema.in === 'request') continue;
    const value = params[key];
    if (value === undefined) continue;

    switch (schema.in) {
      case 'path':
        result.params[key] = value;
        break;
      case 'query':
      case 'queries':
        result.query[key] = value;
        break;
      case 'body':
      case 'body-prop':
        if (schema.in === 'body') {
          result.body = value;
        } else {
          result.body[key] = value;
        }
        break;
      case 'header':
        break;
      case 'formData':
        result.body[key] = value;
        break;
    }
  }

  return result;
}
